<template>
  <div class="app-container">
    <Container
      :title="t('supplier.supplierMaster')"
      :hasLine="true"
    >
      <template #header>
        <SearchForm
          :formModel="searchFormModel"
          :formStruct="searchFormStruct"
          :hasCreate="false /*$hasPermission(['supplier_M'])*/"
          :submitButton="t('common.search')"
          :resetButton="t('common.reset')"
          @submit="submitSearch"
          @reset="resetForm"
        />
      </template>
      <template #main>
        <el-row>
          <el-col
            :span="12"
          >
            <el-button
              v-if="$hasPermission(['MD_VEN_W'])"
              type="primary"
              size="large"
              style="margin-bottom: 10px"
              @click="handleAppend"
            >
              {{ t('common.append') }}
            </el-button>
            <el-upload
              v-if="$hasPermission(['MD_VEN_W'])"
              class="inline-block"
              :action="getUploadUri()"
              :headers="getUploadHeader()"
              :on-success="handleAvatarSuccess"
              :on-error="handleAvatarError"
              :show-file-list="false"
              accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              :before-upload="checkFile"
            >
              <el-button
                class="margin-change"
                type="primary"
                size="large"
              >
                {{ t('common.batchImport') }}
              </el-button>
            </el-upload>
          </el-col>
          <el-col
            :span="12"
            style="display: flex;justify-content: flex-end;align-items: center;"
          >
            <el-button
              type="primary"
              plain
              @click="handleExport"
            >
              {{ t('common.downloadTemplate') }}
            </el-button>
          </el-col>
        </el-row>
        <SimpleTable
          :listLoading="listLoading"
          :tableData="tableData"
          :columnStruct="columnStruct"
          :operateColumnWidth="$hasPermission(['MD_VEN_W']) ? 220:80"
        >
          <template
            #default="slotProps"
          >
            <el-button
              v-if="$hasPermission(['MD_VEN_W'])"
              @click="handleEdit(slotProps.row)"
              type="primary"
              size="mini"
            >
              {{ t('common.edit') }}
            </el-button>
          </template>
        </SimpleTable>
        <el-pagination
          :total="total"
          v-show="total > 0"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :page-size="limit"
          :current-page="page"
          :page-sizes="[10, 20, 50, 100, 200]"
          layout="total, sizes, prev, pager, next, jumper"
          style="text-align: right;"
        />
      </template>
    </Container>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, toRefs, onMounted, onActivated } from 'vue'
import { useI18n } from 'vue-i18n'
import { createModel } from '@/utils/component'
import Container from '@/components/form/container/Container.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import { SupplierTableViewModel, SupplierPageParamModel } from '@/model/master-data/supplierModel'
import { useRouter } from 'vue-router'
import { getSupplierPageList, exportSupplierTemplate, excelSupplierFileUpload } from '@/apis/main-data/supplier'
import { ElMessage } from 'element-plus'
import { useStore } from 'vuex'
import { RootObject } from '@/model/rootObject'

export default defineComponent({
  name: 'SupplierPageList',
  components: {
    SearchForm,
    SimpleTable,
    Container
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const searchStruct = [
      { prop: 'name', label: t('company.companyName'), component: 'el-input', type: 'text', model: '' },
      { prop: 'accountNumber', label: t('supplier.supplierCode'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'isDelete',
        label: t('common.deleteStatus'),
        component: 'el-select',
        options: [
          { label: t('common.yes'), value: true },
          { label: t('common.no'), value: false }
        ]
      }
    ]
    const supplierTableStruct = [
      { prop: 'index', label: t('common.indexNumber') },
      { prop: 'name', label: t('company.companyName') },
      { prop: 'accountNumber', label: t('supplier.supplierCode') },
      { prop: 'paymentTerm', label: t('bank.paymentTerms') },
      { prop: 'comment', label: t('common.notes') },
      { prop: 'isDelete', label: t('common.deleteStatus') }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as SupplierPageParamModel,
      tableData: Array<SupplierTableViewModel>(),
      columnStruct: supplierTableStruct,
      listLoading: true,
      limit: 10,
      total: 0,
      page: 1,
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }
        dataMap.listLoading = true
        const params: SupplierPageParamModel = {
          accountNumber: dataMap.searchFormModel.accountNumber,
          isDelete: dataMap.searchFormModel.isDelete,
          name: dataMap.searchFormModel.name,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        const data = await getSupplierPageList(params)
        if (data && data.data && data.data.list.length > 0) {
          dataMap.tableData = data.data.list.map(
            (item, index) => ({
              id: item.id,
              index: index + 1,
              name: item.name,
              comment: item.comment,
              accountNumber: item.accountNumber,
              paymentTerm: item.paymentTerm,
              isDelete: item.isDelete ? t('common.yes') : t('common.no')
            })
          )
        } else {
          dataMap.tableData = []
        }
        dataMap.total = data?.data.totalCount ?? 0

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      },
      getUploadUri() {
        return excelSupplierFileUpload()
      },
      getUploadHeader() {
        return { Authorization: useStore().state.user.token }
      },
      checkFile(file: any) {
        if (file) {
          console.log('file type')
          console.log(file)
        }
      },
      async handleAvatarSuccess(response: RootObject<number>, file: any) {
        if (response.data > 0) {
          ElMessage({
            message: t('fileUpload.uploadSuccess'),
            type: 'success',
            duration: 2000
          })
          await dataMap.getPageList(null, 10)
        } else if (response.msg && response.msg !== 'success') {
          ElMessage({
            message: response.msg,
            type: 'error',
            duration: 2000
          })
        } else {
          ElMessage({
            message: file.name + ':' + t('fileUpload.uploadError'),
            type: 'error',
            duration: 2000
          })
        }
      },
      handleAvatarError(response: RootObject<number>, file: any) {
        if (response.msg) {
          ElMessage({
            message: response.msg,
            type: 'error',
            duration: 2000
          })
        } else {
          ElMessage({
            message: file.name + ':' + t('fileUpload.uploadError'),
            type: 'error',
            duration: 2000
          })
        }
      }
    })
    const handleAppend = () => {
      router.push({ name: 'SupplierCreate' })
    }
    const handleEdit = (row: SupplierTableViewModel) => {
      router.push({ path: `supplierUpdate/${row.id}` })
    }
    const handleExport = async() => {
      const urlBlob = await exportSupplierTemplate()
      if (urlBlob) {
        const link = document.createElement('a')
        link.href = urlBlob
        link.download = '供应商模板.xlsx'
        link.click()
        window.URL.revokeObjectURL(urlBlob)
      } else {
        ElMessage({
          message: '获取excel失败',
          type: 'error',
          duration: 2000
        })
      }
    }
    onMounted(() => {
      dataMap.getPageList(null, 10)
    })
    onActivated(async() => {
      if (history.state?.refresh === true) {
        await dataMap.getPageList()
        // 用完删除
        const state = { ...history.state }
        delete state.refresh
        history.replaceState(state, '')
      }
    })
    return {
      t,
      handleAppend,
      handleExport,
      handleEdit,
      ...toRefs(dataMap)
    }
  }
})
</script>

<style
  scoped
  lang="scss"
>
.inline-block {
  display: inline-block;
  margin-right: 10px;
}
.margin-change {
  display: inline-block;
  margin-left: 20px;
  margin-bottom: 10px;
}
</style>
